/**
 * DAWTimeline.jsx - Production-ready multitrack timeline DAW
 * 
 * A professional Digital Audio Workstation layout with:
 * - Horizontal timeline with shared ruler across all tracks
 * - Track lanes with waveforms stacked vertically
 * - Track headers with volume, pan, mute, solo, arm controls
 * - Shared playhead that moves across all tracks
 * - Beat/bar grid with snapping
 * - Zoom and scroll controls
 */

import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';

// =============================================================================
// CONSTANTS
// =============================================================================

const TRACK_HEADER_WIDTH = 200;
const TRACK_HEIGHT = 80;
const RULER_HEIGHT = 30;
const PIXELS_PER_SECOND_BASE = 100; // At zoom level 1

// =============================================================================
// TIMELINE RULER COMPONENT
// =============================================================================

export const TimelineRuler = ({
  duration,
  pixelsPerSecond,
  bpm = 120,
  timeSignature = [4, 4],
  showBeats = true,
  scrollLeft = 0
}) => {
  const canvasRef = useRef(null);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    
    ctx.clearRect(0, 0, width, height);
    
    // Background
    ctx.fillStyle = '#1f2937';
    ctx.fillRect(0, 0, width, height);
    
    // Calculate intervals based on zoom level
    const secondsPerBeat = 60 / bpm;
    const secondsPerBar = secondsPerBeat * timeSignature[0];
    
    // Determine major/minor tick intervals based on zoom
    let majorInterval, minorInterval;
    if (pixelsPerSecond < 30) {
      majorInterval = 10; // Every 10 seconds
      minorInterval = 5;
    } else if (pixelsPerSecond < 60) {
      majorInterval = 5;
      minorInterval = 1;
    } else if (pixelsPerSecond < 150) {
      majorInterval = 1;
      minorInterval = 0.5;
    } else {
      majorInterval = secondsPerBar;
      minorInterval = secondsPerBeat;
    }
    
    // Draw minor ticks
    ctx.strokeStyle = '#374151';
    ctx.lineWidth = 1;
    for (let t = 0; t <= duration; t += minorInterval) {
      const x = t * pixelsPerSecond - scrollLeft;
      if (x < 0 || x > width) continue;
      
      ctx.beginPath();
      ctx.moveTo(x, height - 8);
      ctx.lineTo(x, height);
      ctx.stroke();
    }
    
    // Draw major ticks and labels
    ctx.strokeStyle = '#6b7280';
    ctx.fillStyle = '#9ca3af';
    ctx.font = '10px monospace';
    ctx.textAlign = 'center';
    
    for (let t = 0; t <= duration; t += majorInterval) {
      const x = t * pixelsPerSecond - scrollLeft;
      if (x < -50 || x > width + 50) continue;
      
      ctx.beginPath();
      ctx.moveTo(x, height - 15);
      ctx.lineTo(x, height);
      ctx.stroke();
      
      // Time label
      const mins = Math.floor(t / 60);
      const secs = Math.floor(t % 60);
      const ms = Math.floor((t % 1) * 1000);
      
      let label;
      if (showBeats && pixelsPerSecond >= 150) {
        const bar = Math.floor(t / secondsPerBar) + 1;
        const beat = Math.floor((t % secondsPerBar) / secondsPerBeat) + 1;
        label = `${bar}.${beat}`;
      } else {
        label = `${mins}:${secs.toString().padStart(2, '0')}`;
      }
      
      ctx.fillText(label, x, 12);
    }
    
    // Bottom border
    ctx.strokeStyle = '#374151';
    ctx.beginPath();
    ctx.moveTo(0, height - 1);
    ctx.lineTo(width, height - 1);
    ctx.stroke();
    
  }, [duration, pixelsPerSecond, bpm, timeSignature, showBeats, scrollLeft]);
  
  return (
    <canvas
      ref={canvasRef}
      width={2000}
      height={RULER_HEIGHT}
      style={{ width: '100%', height: RULER_HEIGHT }}
      className="bg-gray-800"
    />
  );
};

// =============================================================================
// TRACK HEADER COMPONENT
// =============================================================================

export const TrackHeader = ({
  track,
  index,
  isSelected,
  isArmed,
  onSelect,
  onMute,
  onSolo,
  onArm,
  onVolumeChange,
  onPanChange,
  onNameChange,
  onColorChange,
  onDelete
}) => {
  const [isEditing, setIsEditing] = useState(false);
  
  const trackColors = {
    purple: { bg: '#7c3aed', border: '#8b5cf6' },
    blue: { bg: '#2563eb', border: '#3b82f6' },
    green: { bg: '#16a34a', border: '#22c55e' },
    red: { bg: '#dc2626', border: '#ef4444' },
    yellow: { bg: '#ca8a04', border: '#eab308' },
    cyan: { bg: '#0891b2', border: '#06b6d4' },
    orange: { bg: '#ea580c', border: '#f97316' },
    pink: { bg: '#db2777', border: '#ec4899' }
  };
  
  const colors = trackColors[track.color] || trackColors.purple;
  
  return (
    <div 
      className={`flex flex-col bg-gray-800 border-b border-gray-700 cursor-pointer select-none transition-colors ${
        isSelected ? 'bg-gray-750' : 'hover:bg-gray-750'
      }`}
      style={{ 
        height: TRACK_HEIGHT,
        width: TRACK_HEADER_WIDTH,
        borderLeft: `3px solid ${colors.border}`
      }}
      onClick={() => onSelect?.(track.id)}
    >
      {/* Track name and type */}
      <div className="flex items-center gap-2 px-2 pt-1.5">
        <span className="text-sm">
          {track.type === 'midi' ? '🎹' : track.type === 'virtual' ? '🎛️' : '🎵'}
        </span>
        {isEditing ? (
          <input
            type="text"
            value={track.name}
            onChange={(e) => onNameChange?.(track.id, e.target.value)}
            onBlur={() => setIsEditing(false)}
            onKeyDown={(e) => e.key === 'Enter' && setIsEditing(false)}
            className="flex-1 bg-gray-900 text-xs px-1 py-0.5 rounded border border-purple-500 focus:outline-none"
            autoFocus
          />
        ) : (
          <span 
            className="flex-1 text-xs font-medium text-white truncate"
            onDoubleClick={() => setIsEditing(true)}
          >
            {track.name}
          </span>
        )}
        <span className="text-[10px] text-gray-500">{index + 1}</span>
      </div>
      
      {/* Controls row */}
      <div className="flex items-center gap-1 px-2 py-1">
        {/* Mute */}
        <button
          onClick={(e) => { e.stopPropagation(); onMute?.(track.id); }}
          className={`w-5 h-5 rounded text-[10px] font-bold transition-colors ${
            track.muted ? 'bg-red-600 text-white' : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
          }`}
          title="Mute (M)"
        >
          M
        </button>
        
        {/* Solo */}
        <button
          onClick={(e) => { e.stopPropagation(); onSolo?.(track.id); }}
          className={`w-5 h-5 rounded text-[10px] font-bold transition-colors ${
            track.solo ? 'bg-yellow-500 text-black' : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
          }`}
          title="Solo (S)"
        >
          S
        </button>
        
        {/* Arm for recording */}
        <button
          onClick={(e) => { e.stopPropagation(); onArm?.(track.id); }}
          className={`w-5 h-5 rounded text-[10px] font-bold transition-colors ${
            isArmed ? 'bg-red-600 text-white animate-pulse' : 'bg-gray-700 text-red-400 hover:bg-gray-600'
          }`}
          title="Arm for Recording (R)"
        >
          ●
        </button>
        
        {/* Color picker */}
        <select
          value={track.color || 'purple'}
          onChange={(e) => { e.stopPropagation(); onColorChange?.(track.id, e.target.value); }}
          className="w-5 h-5 bg-gray-700 rounded text-[10px] cursor-pointer border-0 p-0"
          title="Track Color"
          onClick={(e) => e.stopPropagation()}
        >
          <option value="purple">🟣</option>
          <option value="blue">🔵</option>
          <option value="green">🟢</option>
          <option value="red">🔴</option>
          <option value="yellow">🟡</option>
          <option value="cyan">🩵</option>
          <option value="orange">🟠</option>
          <option value="pink">🩷</option>
        </select>
        
        {/* Delete */}
        <button
          onClick={(e) => { e.stopPropagation(); onDelete?.(track.id); }}
          className="w-5 h-5 rounded text-[10px] bg-gray-700 text-gray-500 hover:bg-red-600 hover:text-white transition-colors ml-auto"
          title="Delete Track"
        >
          ✕
        </button>
      </div>
      
      {/* Volume and Pan */}
      <div className="flex items-center gap-2 px-2 pb-1">
        <div className="flex items-center gap-1 flex-1">
          <span className="text-[9px] text-gray-500 w-3">V</span>
          <input
            type="range"
            min="0"
            max="2"
            step="0.01"
            value={track.volume}
            onChange={(e) => onVolumeChange?.(track.id, parseFloat(e.target.value))}
            onClick={(e) => e.stopPropagation()}
            className="flex-1 h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-purple-500"
            title={`Volume: ${Math.round(track.volume * 100)}%`}
          />
          <span className="text-[9px] text-gray-500 w-6">{Math.round(track.volume * 100)}</span>
        </div>
        
        <div className="flex items-center gap-1 flex-1">
          <span className="text-[9px] text-gray-500 w-3">P</span>
          <input
            type="range"
            min="-1"
            max="1"
            step="0.01"
            value={track.pan || 0}
            onChange={(e) => onPanChange?.(track.id, parseFloat(e.target.value))}
            onClick={(e) => e.stopPropagation()}
            className="flex-1 h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-cyan-500"
            title={`Pan: ${track.pan > 0 ? 'R' : track.pan < 0 ? 'L' : 'C'} ${Math.abs(Math.round((track.pan || 0) * 100))}`}
          />
          <span className="text-[9px] text-gray-500 w-6">
            {(track.pan || 0) > 0 ? 'R' : (track.pan || 0) < 0 ? 'L' : 'C'}
          </span>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// TRACK LANE (WAVEFORM AREA)
// =============================================================================

export const TrackLane = ({
  track,
  pixelsPerSecond,
  duration,
  currentTime,
  isSelected,
  isRecording,
  recordingWaveform = [],
  scrollLeft = 0,
  onSeek,
  onRegionSelect
}) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [selection, setSelection] = useState(null);
  
  const trackColors = {
    purple: { wave: '#a855f7', bg: 'rgba(168, 85, 247, 0.15)' },
    blue: { wave: '#3b82f6', bg: 'rgba(59, 130, 246, 0.15)' },
    green: { wave: '#22c55e', bg: 'rgba(34, 197, 94, 0.15)' },
    red: { wave: '#ef4444', bg: 'rgba(239, 68, 68, 0.15)' },
    yellow: { wave: '#eab308', bg: 'rgba(234, 179, 8, 0.15)' },
    cyan: { wave: '#06b6d4', bg: 'rgba(6, 182, 212, 0.15)' },
    orange: { wave: '#f97316', bg: 'rgba(249, 115, 22, 0.15)' },
    pink: { wave: '#ec4899', bg: 'rgba(236, 72, 153, 0.15)' }
  };
  
  const colors = trackColors[track.color] || trackColors.purple;
  
  // Generate peaks from buffer
  const peaks = useMemo(() => {
    if (!track.buffer) return null;
    
    const buffer = track.buffer;
    const channelData = buffer.getChannelData(0);
    // Use buffer duration instead of timeline duration to prevent stretching
    const totalWidth = buffer.duration * pixelsPerSecond;
    const targetPeaks = Math.floor(totalWidth);
    const samplesPerPeak = Math.floor(channelData.length / targetPeaks);
    
    const peakData = [];
    for (let i = 0; i < targetPeaks; i++) {
      const start = i * samplesPerPeak;
      const end = Math.min(start + samplesPerPeak, channelData.length);
      
      let min = 0, max = 0;
      for (let j = start; j < end; j++) {
        if (channelData[j] < min) min = channelData[j];
        if (channelData[j] > max) max = channelData[j];
      }
      
      peakData.push({ min, max });
    }
    
    return peakData;
  }, [track.buffer, duration, pixelsPerSecond]);
  
  // Draw waveform
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    
    ctx.clearRect(0, 0, width, height);
    
    // Background
    ctx.fillStyle = isSelected ? 'rgba(139, 92, 246, 0.1)' : '#111827';
    ctx.fillRect(0, 0, width, height);
    
    // Draw selection region
    if (selection) {
      const startX = selection.start * pixelsPerSecond - scrollLeft;
      const endX = selection.end * pixelsPerSecond - scrollLeft;
      ctx.fillStyle = 'rgba(139, 92, 246, 0.3)';
      ctx.fillRect(startX, 0, endX - startX, height);
    }
    
    // Draw center line
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, height / 2);
    ctx.lineTo(width, height / 2);
    ctx.stroke();
    
    // Draw waveform
    if (isRecording && recordingWaveform.length > 0) {
      // Recording visualization
      ctx.fillStyle = '#ef4444';
      const barWidth = Math.max(2, width / recordingWaveform.length);
      
      recordingWaveform.forEach((value, i) => {
        const normalized = (value - 128) / 128;
        const barHeight = Math.abs(normalized) * height * 0.8;
        const x = i * barWidth;
        const y = (height - barHeight) / 2;
        ctx.fillRect(x, y, barWidth - 1, barHeight);
      });
    } else if (peaks && peaks.length > 0) {
      ctx.fillStyle = colors.wave;
      
      const startPeak = Math.floor(scrollLeft);
      const endPeak = Math.min(startPeak + width, peaks.length);
      
      for (let i = startPeak; i < endPeak; i++) {
        const peak = peaks[i];
        if (!peak) continue;
        
        const x = i - scrollLeft;
        const minY = height / 2 + (peak.min * height / 2) * 0.9;
        const maxY = height / 2 + (peak.max * height / 2) * 0.9;
        const barHeight = Math.max(1, minY - maxY);
        
        ctx.fillRect(x, maxY, 1, barHeight);
      }
    } else if (track.type === 'midi') {
      // MIDI placeholder
      ctx.fillStyle = 'rgba(96, 165, 250, 0.3)';
      ctx.font = '11px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('🎹 MIDI - Draw or record notes', width / 2, height / 2 + 4);
    } else if (track.type === 'virtual') {
      // Virtual instrument placeholder
      ctx.fillStyle = 'rgba(74, 222, 128, 0.3)';
      ctx.font = '11px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('🎛️ Virtual Instrument - Play or sequence', width / 2, height / 2 + 4);
    } else {
      // Empty track
      ctx.fillStyle = 'rgba(156, 163, 175, 0.3)';
      ctx.font = '11px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Empty - Import, record, or drag audio here', width / 2, height / 2 + 4);
    }
    
    // Draw grid lines (beat markers)
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.05)';
    ctx.lineWidth = 1;
    const gridInterval = 1; // 1 second
    for (let t = 0; t <= duration; t += gridInterval) {
      const x = t * pixelsPerSecond - scrollLeft;
      if (x < 0 || x > width) continue;
      
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, height);
      ctx.stroke();
    }
    
    // Draw mute overlay
    if (track.muted) {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
      ctx.fillRect(0, 0, width, height);
      ctx.fillStyle = '#ef4444';
      ctx.font = 'bold 12px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('MUTED', width / 2, height / 2 + 4);
    }
    
  }, [peaks, isSelected, isRecording, recordingWaveform, selection, track.muted, track.type, colors, pixelsPerSecond, duration, scrollLeft]);
  
  // Handle click to seek
  const handleClick = useCallback((e) => {
    if (isDragging) return;
    
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    
    const x = e.clientX - rect.left + scrollLeft;
    const time = x / pixelsPerSecond;
    
    onSeek?.(Math.max(0, Math.min(duration, time)));
  }, [isDragging, scrollLeft, pixelsPerSecond, duration, onSeek]);
  
  // Handle drag for region selection
  const handleMouseDown = useCallback((e) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    
    const x = e.clientX - rect.left + scrollLeft;
    const time = x / pixelsPerSecond;
    
    setIsDragging(true);
    setDragStart(time);
    setSelection({ start: time, end: time });
  }, [scrollLeft, pixelsPerSecond]);
  
  const handleMouseMove = useCallback((e) => {
    if (!isDragging || dragStart === null) return;
    
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    
    const x = e.clientX - rect.left + scrollLeft;
    const time = Math.max(0, Math.min(duration, x / pixelsPerSecond));
    
    setSelection({
      start: Math.min(dragStart, time),
      end: Math.max(dragStart, time)
    });
  }, [isDragging, dragStart, scrollLeft, pixelsPerSecond, duration]);
  
  const handleMouseUp = useCallback(() => {
    if (isDragging && selection && Math.abs(selection.end - selection.start) > 0.01) {
      onRegionSelect?.(track.id, selection);
    }
    setIsDragging(false);
    setDragStart(null);
  }, [isDragging, selection, track.id, onRegionSelect]);
  
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);
  
  return (
    <div
      ref={containerRef}
      className={`relative border-b border-gray-700 cursor-crosshair ${
        isSelected ? 'ring-1 ring-purple-500 ring-inset' : ''
      }`}
      style={{ height: TRACK_HEIGHT }}
      onClick={handleClick}
      onMouseDown={handleMouseDown}
    >
      <canvas
        ref={canvasRef}
        width={2000}
        height={TRACK_HEIGHT}
        style={{ width: '100%', height: '100%' }}
      />
    </div>
  );
};

// =============================================================================
// PLAYHEAD COMPONENT
// =============================================================================

export const Playhead = ({ currentTime, pixelsPerSecond, scrollLeft, height }) => {
  const x = currentTime * pixelsPerSecond - scrollLeft;
  
  if (x < 0 || x > 2000) return null;
  
  return (
    <div
      className="absolute top-0 pointer-events-none z-20"
      style={{
        left: x,
        height: height,
        width: 2
      }}
    >
      {/* Playhead line */}
      <div className="w-0.5 h-full bg-white shadow-lg shadow-white/30" />
      
      {/* Playhead triangle */}
      <div 
        className="absolute -top-0 -left-1.5 w-0 h-0"
        style={{
          borderLeft: '6px solid transparent',
          borderRight: '6px solid transparent',
          borderTop: '8px solid white'
        }}
      />
    </div>
  );
};

// =============================================================================
// MAIN TIMELINE COMPONENT
// =============================================================================

export const DAWTimeline = ({
  tracks,
  duration,
  currentTime,
  isPlaying,
  bpm = 120,
  armedTrackIds = [],
  selectedTrackId,
  recordingTrackId,
  recordingWaveform = [],
  onSeek,
  onTrackSelect,
  onTrackMute,
  onTrackSolo,
  onTrackArm,
  onTrackVolumeChange,
  onTrackPanChange,
  onTrackNameChange,
  onTrackColorChange,
  onTrackDelete,
  onRegionSelect,
  onAddTrack
}) => {
  const containerRef = useRef(null);
  const timelineRef = useRef(null);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [zoomLevel, setZoomLevel] = useState(1);
  
  const pixelsPerSecond = PIXELS_PER_SECOND_BASE * zoomLevel;
  const totalWidth = duration * pixelsPerSecond;
  const totalHeight = RULER_HEIGHT + (tracks.length * TRACK_HEIGHT);
  
  // Auto-scroll to follow playhead
  useEffect(() => {
    if (!isPlaying || !timelineRef.current) return;
    
    const playheadX = currentTime * pixelsPerSecond;
    const containerWidth = timelineRef.current.clientWidth - TRACK_HEADER_WIDTH;
    
    if (playheadX > scrollLeft + containerWidth - 100) {
      setScrollLeft(playheadX - 100);
    } else if (playheadX < scrollLeft + 50) {
      setScrollLeft(Math.max(0, playheadX - 50));
    }
  }, [currentTime, isPlaying, pixelsPerSecond, scrollLeft]);
  
  // Handle scroll
  const handleScroll = (e) => {
    setScrollLeft(e.target.scrollLeft);
  };
  
  // Zoom controls
  const handleZoomIn = () => setZoomLevel(prev => Math.min(prev * 1.5, 8));
  const handleZoomOut = () => setZoomLevel(prev => Math.max(prev / 1.5, 0.25));
  
  // Handle ruler click for seeking
  const handleRulerClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left + scrollLeft;
    const time = x / pixelsPerSecond;
    onSeek?.(Math.max(0, Math.min(duration, time)));
  };
  
  return (
    <div ref={containerRef} className="flex flex-col h-full bg-gray-900 select-none">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-3 py-2 bg-gray-800 border-b border-gray-700">
        <div className="flex items-center gap-2">
          <button
            onClick={onAddTrack}
            className="px-3 py-1.5 bg-purple-600 hover:bg-purple-500 rounded text-xs font-bold flex items-center gap-1"
          >
            <span>+</span> Add Track
          </button>
        </div>
        
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-400">Zoom:</span>
          <button
            onClick={handleZoomOut}
            disabled={zoomLevel <= 0.25}
            className="w-6 h-6 bg-gray-700 hover:bg-gray-600 rounded text-xs disabled:opacity-50"
          >
            −
          </button>
          <span className="text-xs text-gray-300 w-12 text-center">
            {Math.round(zoomLevel * 100)}%
          </span>
          <button
            onClick={handleZoomIn}
            disabled={zoomLevel >= 8}
            className="w-6 h-6 bg-gray-700 hover:bg-gray-600 rounded text-xs disabled:opacity-50"
          >
            +
          </button>
        </div>
        
        <div className="flex items-center gap-4 text-xs text-gray-400">
          <span>Duration: {Math.floor(duration / 60)}:{Math.floor(duration % 60).toString().padStart(2, '0')}</span>
          <span>Tracks: {tracks.length}</span>
          <span>BPM: {bpm}</span>
        </div>
      </div>
      
      {/* Timeline area */}
      <div ref={timelineRef} className="flex flex-1 overflow-hidden">
        {/* Fixed track headers column */}
        <div 
          className="flex flex-col bg-gray-800 border-r border-gray-700 overflow-y-auto"
          style={{ width: TRACK_HEADER_WIDTH, minWidth: TRACK_HEADER_WIDTH }}
        >
          {/* Corner spacer for ruler */}
          <div 
            className="bg-gray-800 border-b border-gray-700 flex items-center justify-center text-xs text-gray-500"
            style={{ height: RULER_HEIGHT, minHeight: RULER_HEIGHT }}
          >
            Tracks
          </div>
          
          {/* Track headers */}
          {tracks.map((track, index) => (
            <TrackHeader
              key={track.id}
              track={track}
              index={index}
              isSelected={selectedTrackId === track.id}
              isArmed={armedTrackIds.includes(track.id)}
              onSelect={onTrackSelect}
              onMute={onTrackMute}
              onSolo={onTrackSolo}
              onArm={onTrackArm}
              onVolumeChange={onTrackVolumeChange}
              onPanChange={onTrackPanChange}
              onNameChange={onTrackNameChange}
              onColorChange={onTrackColorChange}
              onDelete={onTrackDelete}
            />
          ))}
          
          {/* Empty state */}
          {tracks.length === 0 && (
            <div className="flex-1 flex items-center justify-center p-4">
              <button
                onClick={onAddTrack}
                className="text-center text-gray-500 hover:text-gray-400"
              >
                <div className="text-3xl mb-2">+</div>
                <div className="text-xs">Add Track</div>
              </button>
            </div>
          )}
        </div>
        
        {/* Scrollable timeline content */}
        <div 
          className="flex-1 overflow-auto relative"
          onScroll={handleScroll}
        >
          <div style={{ width: Math.max(totalWidth, 2000), minHeight: totalHeight }}>
            {/* Timeline ruler */}
            <div 
              className="sticky top-0 z-10 cursor-pointer"
              onClick={handleRulerClick}
              style={{ height: RULER_HEIGHT }}
            >
              <TimelineRuler
                duration={duration}
                pixelsPerSecond={pixelsPerSecond}
                bpm={bpm}
                scrollLeft={scrollLeft}
              />
            </div>
            
            {/* Track lanes */}
            {tracks.map((track) => (
              <TrackLane
                key={track.id}
                track={track}
                pixelsPerSecond={pixelsPerSecond}
                duration={duration}
                currentTime={currentTime}
                isSelected={selectedTrackId === track.id}
                isRecording={recordingTrackId === track.id}
                recordingWaveform={recordingWaveform}
                scrollLeft={scrollLeft}
                onSeek={onSeek}
                onRegionSelect={onRegionSelect}
              />
            ))}
          </div>
          
          {/* Playhead overlay */}
          <Playhead
            currentTime={currentTime}
            pixelsPerSecond={pixelsPerSecond}
            scrollLeft={scrollLeft}
            height={totalHeight}
          />
        </div>
      </div>
    </div>
  );
};

export default DAWTimeline;
